import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { Icon } from 'semantic-ui-react';
import { makeAssetLink } from '../utils/helpers';
import { allCountries } from './common/CountryData';
import {
  ACCOUNT_TYPES,
  AUCTION_ACCESS_ENROLL_URL,
  REGISTRATION_ERRORS,
  completeRegistration,
  fetchRegistrationConfig,
  isPlausibleE164,
  sendPhoneCode,
  startRegistration,
  toE164,
  verifyAuctionAccess,
  verifyPhoneCode,
} from '../utils/helpers/registration';
import { isValidEmail, startSsoLogin } from '../utils/helpers/ssoAuth';

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 460,
    maxHeight: '90vh',
    overflowY: 'auto',
    borderRadius: 8,
    border: 'none',
    padding: '2.5rem 2rem',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 99,
  },
};

const styles = {
  logo: { display: 'block', maxWidth: 240, maxHeight: 140, margin: '0 auto 2rem' },
  form: { maxWidth: 340, margin: '0 auto' },
  heading: { textAlign: 'center', fontSize: 20, fontWeight: 500, margin: '0 0 0.25rem' },
  subheading: { textAlign: 'center', color: '#666', margin: '0 0 1.5rem' },
  field: { marginBottom: 14 },
  label: { display: 'block', fontSize: 13, color: '#666', marginBottom: 4 },
  input: {
    width: '100%',
    height: 46,
    padding: '0 12px',
    fontSize: 15,
    border: '1px solid #DEDEDF',
    borderRadius: 5,
    boxSizing: 'border-box',
  },
  phoneRow: { display: 'flex', gap: 8 },
  dialSelect: { flex: '0 0 130px' },
  validation: { color: '#9f3a38', textAlign: 'center', marginTop: 12 },
  centerText: { textAlign: 'center' },
  primaryButton: {
    display: 'block',
    width: '100%',
    maxWidth: 340,
    margin: '18px auto 0',
    padding: '12px 16px',
    borderRadius: 5,
    border: 'none',
    background: '#4183c4',
    color: '#fff',
    cursor: 'pointer',
    fontSize: 15,
  },
  choiceButton: {
    display: 'block',
    width: '100%',
    maxWidth: 340,
    margin: '12px auto 0',
    padding: '12px 16px',
    borderRadius: 5,
    border: '1px solid #DEDEDF',
    background: '#fff',
    cursor: 'pointer',
    fontSize: 15,
  },
  linksRow: { marginTop: 24, textAlign: 'center' },
  link: {
    color: '#4183c4',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
  },
  termsFrame: {
    width: '100%',
    height: 320,
    border: '1px solid #DEDEDF',
    borderRadius: 5,
    marginBottom: 12,
  },
};

const STEP = {
  LOADING: 'loading',
  TERMS: 'terms',
  EMAIL: 'email',
  PHONE: 'phone',
  PHONE_VERIFY: 'phoneVerify',
  ASK_DEALER: 'askDealer',
  ASK_AUCTION_ACCESS: 'askAuctionAccess',
  AUCTION_ACCESS: 'auctionAccess',
  DETAILS: 'details',
  NO_PUBLIC: 'noPublic',
  DONE: 'done',
  ERROR: 'error',
};

/** Fields whose value is a country name rather than free text. */
const COUNTRY_FIELDS = ['country'];

/** Country names, de-duplicated, for the country dropdown. */
const COUNTRY_NAMES = Array.from(new Set(allCountries.map(country => country.name.trim()))).sort();

/**
 * Pre-selected country, as ias-connect did. Seeded into `details` rather than defaulted in the
 * `<select>` alone: a value that exists only in the rendered dropdown looks chosen but never reaches
 * state, so submitting reports the field as missing.
 */
const DEFAULT_COUNTRY = 'United States';

/**
 * Dial codes missing from CountryData: the US and UK entries there have an empty dial code, and the
 * builder derives each country's area-code variants as `dialCode + areaCode` — so with a blank base the
 * US variants come out as bare area codes ("907") rather than "1907". Supplying the two bases here
 * keeps the fix local to this screen rather than editing shared data.
 */
const DIAL_CODE_OVERRIDES = { us: '1', gb: '44' };

/**
 * One option per country, keyed by iso2.
 *
 * Two things about CountryData drive this shape. First, it appends a synthetic entry per area code
 * (Canada alone has ~200), so options are de-duplicated by iso2 keeping the first — the real country —
 * and the synthetic ones are dropped. Second, options are valued on iso2 rather than the dial code,
 * because +1 covers the US, Canada and ~20 other NANP territories; a `<select>` valued on "1" resolves
 * to whichever of them appears first, regardless of which was intended.
 */
const DIAL_CODES = allCountries
  .filter((country, index, list) => list.findIndex(other => other.iso2 === country.iso2) === index)
  .map(country => {
    const dialCode = DIAL_CODE_OVERRIDES[country.iso2] || country.dialCode;
    return {
      key: country.iso2,
      label: `+${dialCode} (${country.name.trim()})`,
      dialCode,
    };
  })
  .filter(option => option.dialCode);

/** The default selection, and the one hoisted to the top of the list — as ias-connect did. */
const DEFAULT_DIAL_KEY = 'us';

const DIAL_CODE_OPTIONS = [
  ...DIAL_CODES.filter(option => option.key === DEFAULT_DIAL_KEY),
  ...DIAL_CODES.filter(option => option.key !== DEFAULT_DIAL_KEY),
];

function dialCodeFor(dialKey) {
  const match = DIAL_CODES.find(option => option.key === dialKey);
  return match ? match.dialCode : '1';
}

/**
 * Native self-registration (MP-2328 follow-on). Ports the ias-connect registration flow — terms,
 * email, SMS confirmation of a mobile number, the dealer / AuctionACCESS / public branching, and the
 * marketplace's configured account fields — with marketplace-api owning all the logic.
 *
 * Registration ends by emailing the registrant a link to set their password; they then sign in through
 * the normal SSO flow, so this modal never handles a password.
 *
 * If marketplace-api reports registration unavailable here (a marketplace still on ias-connect, or one
 * that hasn't opted in), `onUnavailable` is called so the caller can open the ias-connect iframe
 * instead. The user never sees a dead end.
 */
export default function RegisterModal(props) {
  const { isOpen, onClose, onUnavailable } = props;

  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(STEP.LOADING);
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [dialKey, setDialKey] = useState(DEFAULT_DIAL_KEY);
  const [localPhone, setLocalPhone] = useState('');
  const [verifiedPhone, setVerifiedPhone] = useState('');
  const [code, setCode] = useState('');
  const [accountType, setAccountType] = useState(ACCOUNT_TYPES.DEALER);
  const [auctionAccessRepId, setAuctionAccessRepId] = useState('');
  const [governmentId, setGovernmentId] = useState('');
  const [auctionAccess, setAuctionAccess] = useState(null);
  const [details, setDetails] = useState({ country: DEFAULT_COUNTRY });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [showEnrollLink, setShowEnrollLink] = useState(false);

  const dialCode = dialCodeFor(dialKey);

  // Held in a ref so the config effect below can depend on `isOpen` alone. Depending on the callback
  // itself would re-run the effect on every parent render — resetting the user to step one mid-flow.
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;

  const reset = useCallback(() => {
    setStep(STEP.LOADING);
    setConfig(null);
    setToken('');
    setEmail('');
    setDialKey(DEFAULT_DIAL_KEY);
    setLocalPhone('');
    setVerifiedPhone('');
    setCode('');
    setAccountType(ACCOUNT_TYPES.DEALER);
    setAuctionAccessRepId('');
    setGovernmentId('');
    setAuctionAccess(null);
    setDetails({ country: DEFAULT_COUNTRY });
    setBusy(false);
    setMessage('');
    setShowEnrollLink(false);
  }, []);

  // Load the marketplace's registration configuration each time the modal opens, so a config change
  // (or a marketplace being switched on) is picked up without a page reload. Deliberately keyed on
  // `isOpen` only — see onUnavailableRef above.
  useEffect(() => {
    if (!isOpen) {
      reset();
      return undefined;
    }

    let cancelled = false;
    fetchRegistrationConfig().then(loaded => {
      if (cancelled) return;
      if (!loaded || !loaded.enabled) {
        // Not available natively — hand the user to the ias-connect iframe rather than stranding them.
        onUnavailableRef.current();
        return;
      }
      setConfig(loaded);
      setStep(loaded.termsUrl ? STEP.TERMS : STEP.EMAIL);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /**
   * Maps a failed step onto what the user sees. A dead registration session is the one case that has
   * to restart the flow, since every later step needs a token.
   */
  const handleError = useCallback(error => {
    setBusy(false);
    const errorCode = error && error.code;

    if (errorCode === REGISTRATION_ERRORS.SESSION_EXPIRED) {
      setToken('');
      setStep(STEP.EMAIL);
      setMessage('Your registration session expired. Please start again.');
      return;
    }

    if (errorCode === REGISTRATION_ERRORS.DISABLED) {
      onUnavailableRef.current();
      return;
    }

    setMessage((error && error.message) || 'Something went wrong. Please try again.');
  }, []);

  /** Where to go once the email (and any phone confirmation) is done. */
  const goToAccountTypeStep = useCallback(() => {
    if (!config) return;

    if (config.dealerRegistrationEnabled) {
      setStep(STEP.ASK_DEALER);
      return;
    }

    // Dealer registration hidden: the only path left is a public account, if the marketplace has them.
    if (config.publicRegistrationEnabled) {
      setAccountType(ACCOUNT_TYPES.PUBLIC);
      setStep(STEP.DETAILS);
      return;
    }

    setStep(STEP.NO_PUBLIC);
  }, [config]);

  const submitEmail = async e => {
    if (e) e.preventDefault();
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setMessage('Please enter a valid email address');
      return;
    }

    setBusy(true);
    setMessage('');
    try {
      const result = await startRegistration(trimmed, Boolean(config.termsUrl));
      setToken(result.registrationToken);
      setBusy(false);

      if (result.phoneVerificationRequired) {
        setStep(STEP.PHONE);
      } else {
        goToAccountTypeStep();
      }
    } catch (error) {
      handleError(error);
    }
  };

  const submitPhone = async e => {
    if (e) e.preventDefault();
    const e164 = toE164(dialCode, localPhone);
    if (!isPlausibleE164(e164)) {
      setMessage('Please enter a valid mobile phone number');
      return;
    }

    setBusy(true);
    setMessage('');
    try {
      const result = await sendPhoneCode(token, e164);
      setToken(result.registrationToken);
      setBusy(false);
      setCode('');
      setStep(STEP.PHONE_VERIFY);
    } catch (error) {
      handleError(error);
    }
  };

  const submitCode = async e => {
    if (e) e.preventDefault();
    if (!code.trim()) {
      setMessage('Please enter the code we texted you');
      return;
    }

    setBusy(true);
    setMessage('');
    try {
      const result = await verifyPhoneCode(token, code.trim());
      setToken(result.registrationToken);
      const e164 = toE164(dialCode, localPhone);
      setVerifiedPhone(e164);
      // Pre-fill the account form's phone field with the number we just confirmed.
      setDetails(current => ({ ...current, phoneNumber: e164 }));
      setBusy(false);
      goToAccountTypeStep();
    } catch (error) {
      handleError(error);
    }
  };

  const chooseDealer = isDealer => {
    setMessage('');

    if (!isDealer) {
      if (config.publicRegistrationEnabled) {
        setAccountType(ACCOUNT_TYPES.PUBLIC);
        setStep(STEP.DETAILS);
      } else {
        setStep(STEP.NO_PUBLIC);
      }
      return;
    }

    if (config.auctionAccessRegistrationEnabled) {
      setStep(STEP.ASK_AUCTION_ACCESS);
    } else {
      setAccountType(ACCOUNT_TYPES.DEALER);
      setStep(STEP.DETAILS);
    }
  };

  const chooseAuctionAccess = hasAuctionAccess => {
    setMessage('');
    setShowEnrollLink(false);

    if (hasAuctionAccess) {
      setAccountType(ACCOUNT_TYPES.AUCTION_ACCESS_DEALER);
      setStep(STEP.AUCTION_ACCESS);
    } else {
      setAccountType(ACCOUNT_TYPES.DEALER);
      setStep(STEP.DETAILS);
    }
  };

  const submitAuctionAccess = async e => {
    if (e) e.preventDefault();
    if (!auctionAccessRepId.trim() || !governmentId.trim()) {
      setMessage('Please enter both your AuctionACCESS number and the last 4 of your ID');
      return;
    }

    setBusy(true);
    setMessage('');
    setShowEnrollLink(false);
    try {
      const result = await verifyAuctionAccess(token, auctionAccessRepId.trim(), governmentId.trim());
      setToken(result.registrationToken);
      setAuctionAccess(result);
      setBusy(false);
      setStep(STEP.DETAILS);
    } catch (error) {
      // A rejected lookup usually means the person isn't enrolled, so offer the enrollment link the way
      // ias-connect did.
      if (error && error.code === REGISTRATION_ERRORS.AUCTION_ACCESS_INVALID) {
        setShowEnrollLink(true);
      }
      handleError(error);
    }
  };

  const activeFields = useMemo(() => {
    if (!config) return [];
    switch (accountType) {
      case ACCOUNT_TYPES.AUCTION_ACCESS_DEALER:
        return config.auctionAccessFields || [];
      case ACCOUNT_TYPES.PUBLIC:
        return config.publicFields || [];
      default:
        return config.dealerFields || [];
    }
  }, [config, accountType]);

  const submitDetails = async e => {
    if (e) e.preventDefault();

    const missing = activeFields
      .filter(field => field.required && !String(details[field.key] || '').trim())
      .map(field => field.label);

    if (missing.length > 0) {
      setMessage(`Please complete: ${missing.join(', ')}`);
      return;
    }

    setBusy(true);
    setMessage('');
    try {
      await completeRegistration(token, accountType, details);
      setBusy(false);
      setStep(STEP.DONE);
    } catch (error) {
      // The account was created but its sign-in identity wasn't — a distinct outcome, because the user
      // should not try to register again.
      if (error && error.code === REGISTRATION_ERRORS.IDENTITY_SETUP_FAILED) {
        setBusy(false);
        setStep(STEP.ERROR);
        setMessage(error.message);
        return;
      }
      handleError(error);
    }
  };

  const setDetail = (key, value) => setDetails(current => ({ ...current, [key]: value }));

  // #region renderers
  const renderTextField = field => (
    <div key={field.key} style={styles.field}>
      <label style={styles.label} htmlFor={`reg-${field.key}`}>
        {field.label}
        {field.required ? ' *' : ''}
      </label>
      <input
        id={`reg-${field.key}`}
        type="text"
        style={styles.input}
        disabled={busy}
        value={details[field.key] || ''}
        onChange={event => setDetail(field.key, event.target.value)}
      />
    </div>
  );

  const renderCountryField = field => (
    <div key={field.key} style={styles.field}>
      <label style={styles.label} htmlFor={`reg-${field.key}`}>
        {field.label}
        {field.required ? ' *' : ''}
      </label>
      <select
        id={`reg-${field.key}`}
        style={styles.input}
        disabled={busy}
        value={details[field.key] || DEFAULT_COUNTRY}
        onChange={event => setDetail(field.key, event.target.value)}
      >
        {COUNTRY_NAMES.map(name => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>
    </div>
  );

  const renderPhoneField = field => {
    // Once a number has been confirmed by SMS it is the account's number; showing it read-only avoids
    // implying the user can change it here (the server uses the verified value regardless).
    if (verifiedPhone) {
      return (
        <div key={field.key} style={styles.field}>
          <label style={styles.label}>{field.label}</label>
          <input type="text" style={{ ...styles.input, background: '#f7f7f7' }} value={verifiedPhone} readOnly />
        </div>
      );
    }

    return renderTextField(field);
  };

  const renderField = field => {
    if (COUNTRY_FIELDS.includes(field.key)) return renderCountryField(field);
    if (field.key === 'phoneNumber') return renderPhoneField(field);
    return renderTextField(field);
  };

  const renderTerms = () => (
    <div>
      <h2 style={styles.heading}>Terms &amp; Conditions</h2>
      <p style={styles.subheading}>Please review and accept to continue.</p>
      <iframe title="Terms and Conditions" src={config.termsUrl} style={styles.termsFrame} />
      <button type="button" style={styles.primaryButton} onClick={() => setStep(STEP.EMAIL)}>
        Accept &amp; Continue
      </button>
      <button type="button" style={styles.choiceButton} onClick={onClose}>
        Decline
      </button>
    </div>
  );

  const renderEmail = () => (
    <form style={styles.form} onSubmit={submitEmail}>
      <h2 style={styles.heading}>Create an account</h2>
      <p style={styles.subheading}>We&apos;ll start with your email address.</p>
      <div style={styles.field}>
        <label style={styles.label} htmlFor="reg-email">
          Email *
        </label>
        <input
          id="reg-email"
          autoFocus
          type="email"
          style={styles.input}
          disabled={busy}
          value={email}
          onChange={event => setEmail(event.target.value)}
        />
      </div>
      {message && <p style={styles.validation}>{message}</p>}
      <button type="submit" style={styles.primaryButton} disabled={busy}>
        {busy ? 'Checking…' : 'Continue'}
      </button>
    </form>
  );

  const renderPhone = () => (
    <form style={styles.form} onSubmit={submitPhone}>
      <h2 style={styles.heading}>Mobile number</h2>
      <p style={styles.subheading}>
        We&apos;ll text you a confirmation code when you continue.
      </p>
      <div style={styles.field}>
        <label style={styles.label} htmlFor="reg-phone">
          Mobile phone *
        </label>
        <div style={styles.phoneRow}>
          <select
            aria-label="Country dial code"
            style={{ ...styles.input, ...styles.dialSelect }}
            disabled={busy}
            value={dialKey}
            onChange={event => setDialKey(event.target.value)}
          >
            {DIAL_CODE_OPTIONS.map(option => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
          <input
            id="reg-phone"
            autoFocus
            type="tel"
            style={styles.input}
            disabled={busy}
            value={localPhone}
            onChange={event => setLocalPhone(event.target.value)}
          />
        </div>
      </div>
      {message && <p style={styles.validation}>{message}</p>}
      <button type="submit" style={styles.primaryButton} disabled={busy}>
        {busy ? 'Sending…' : 'Send code'}
      </button>
    </form>
  );

  const renderPhoneVerify = () => (
    <form style={styles.form} onSubmit={submitCode}>
      <h2 style={styles.heading}>Confirm your number</h2>
      <p style={styles.subheading}>
        Enter the code we texted to {toE164(dialCode, localPhone)}.
      </p>
      <div style={styles.field}>
        <label style={styles.label} htmlFor="reg-code">
          Verification code *
        </label>
        <input
          id="reg-code"
          autoFocus
          type="text"
          inputMode="numeric"
          style={styles.input}
          disabled={busy}
          value={code}
          onChange={event => setCode(event.target.value)}
        />
      </div>
      {message && <p style={styles.validation}>{message}</p>}
      <button type="submit" style={styles.primaryButton} disabled={busy}>
        {busy ? 'Checking…' : 'Confirm'}
      </button>
      <div style={styles.linksRow}>
        <button
          type="button"
          style={styles.link}
          disabled={busy}
          onClick={() => {
            setMessage('');
            setStep(STEP.PHONE);
          }}
        >
          Use a different number
        </button>
      </div>
    </form>
  );

  const renderQuestion = (heading, onYes, onNo) => (
    <div style={styles.centerText}>
      <h2 style={styles.heading}>{heading}</h2>
      <button type="button" style={styles.primaryButton} onClick={onYes}>
        Yes
      </button>
      <button type="button" style={styles.choiceButton} onClick={onNo}>
        No
      </button>
      {message && <p style={styles.validation}>{message}</p>}
    </div>
  );

  const renderAuctionAccess = () => (
    <form style={styles.form} onSubmit={submitAuctionAccess}>
      <h2 style={styles.heading}>AuctionACCESS</h2>
      <p style={styles.subheading}>We&apos;ll use this to find your dealerships.</p>
      <div style={styles.field}>
        <label style={styles.label} htmlFor="reg-aa-rep">
          AuctionACCESS Rep # (9 digits) *
        </label>
        <input
          id="reg-aa-rep"
          autoFocus
          type="text"
          inputMode="numeric"
          style={styles.input}
          disabled={busy}
          value={auctionAccessRepId}
          onChange={event => setAuctionAccessRepId(event.target.value)}
        />
      </div>
      <div style={styles.field}>
        <label style={styles.label} htmlFor="reg-aa-gov">
          Last 4 of Government ID *
        </label>
        <input
          id="reg-aa-gov"
          type="text"
          inputMode="numeric"
          style={styles.input}
          disabled={busy}
          value={governmentId}
          onChange={event => setGovernmentId(event.target.value)}
        />
      </div>
      {message && <p style={styles.validation}>{message}</p>}
      {showEnrollLink && (
        <p style={styles.centerText}>
          Not enrolled?{' '}
          <a href={AUCTION_ACCESS_ENROLL_URL} target="_blank" rel="noreferrer">
            AuctionACCESS Enrollment
          </a>
        </p>
      )}
      <button type="submit" style={styles.primaryButton} disabled={busy}>
        {busy ? 'Verifying…' : 'Continue'}
      </button>
      <div style={styles.linksRow}>
        <button
          type="button"
          style={styles.link}
          disabled={busy}
          onClick={() => chooseAuctionAccess(false)}
        >
          I don&apos;t have an AuctionACCESS number
        </button>
      </div>
    </form>
  );

  const renderDetails = () => (
    <form style={styles.form} onSubmit={submitDetails}>
      <h2 style={styles.heading}>
        {accountType === ACCOUNT_TYPES.AUCTION_ACCESS_DEALER ? 'Confirm your information' : 'Your details'}
      </h2>
      {auctionAccess && (
        <p style={styles.subheading}>
          Registering {auctionAccess.firstName} {auctionAccess.lastName}
          {auctionAccess.dealers && auctionAccess.dealers.length > 0 && (
            <>
              <br />
              {auctionAccess.dealers.length === 1
                ? auctionAccess.dealers[0].dealerName
                : `${auctionAccess.dealers.length} dealerships`}
            </>
          )}
        </p>
      )}
      {activeFields.map(renderField)}
      {config.taxIdEnabled && renderTextField({ key: 'taxId', label: 'Tax ID', required: false })}
      {message && <p style={styles.validation}>{message}</p>}
      <button type="submit" style={styles.primaryButton} disabled={busy}>
        {busy ? 'Creating your account…' : 'Create account'}
      </button>
    </form>
  );

  const renderNoPublic = () => (
    <div style={styles.centerText}>
      <h2 style={styles.heading}>Dealers only</h2>
      <p>
        This marketplace is open to licensed dealers only. If you believe you should have access, please
        contact the auction.
      </p>
      <button type="button" style={styles.choiceButton} onClick={onClose}>
        Close
      </button>
    </div>
  );

  const renderDone = () => (
    <div style={styles.centerText}>
      <h2 style={styles.heading}>Check your inbox</h2>
      <p>
        Your account is ready. We&apos;ve emailed <strong>{email.trim()}</strong> a link to set your
        password — follow it, then come back and sign in.
      </p>
      <button
        type="button"
        style={styles.primaryButton}
        disabled={busy}
        onClick={() => {
          setBusy(true);
          startSsoLogin().catch(() => {
            setBusy(false);
            setMessage('We couldn’t start sign-in. Please try again.');
          });
        }}
      >
        {busy ? 'Signing in…' : 'I’ve set my password — sign in'}
      </button>
      {message && <p style={styles.validation}>{message}</p>}
    </div>
  );

  const renderError = () => (
    <div style={styles.centerText}>
      <p style={styles.validation}>{message}</p>
      <button type="button" style={styles.choiceButton} onClick={onClose}>
        Close
      </button>
    </div>
  );

  const renderBody = () => {
    if (step === STEP.LOADING || !config) {
      return (
        <div style={styles.centerText}>
          <Icon name="spinner" loading size="large" />
        </div>
      );
    }

    switch (step) {
      case STEP.TERMS:
        return renderTerms();
      case STEP.PHONE:
        return renderPhone();
      case STEP.PHONE_VERIFY:
        return renderPhoneVerify();
      case STEP.ASK_DEALER:
        return renderQuestion('Are you a dealer?', () => chooseDealer(true), () => chooseDealer(false));
      case STEP.ASK_AUCTION_ACCESS:
        return renderQuestion(
          'Are you an AuctionACCESS dealer?',
          () => chooseAuctionAccess(true),
          () => chooseAuctionAccess(false)
        );
      case STEP.AUCTION_ACCESS:
        return renderAuctionAccess();
      case STEP.DETAILS:
        return renderDetails();
      case STEP.NO_PUBLIC:
        return renderNoPublic();
      case STEP.DONE:
        return renderDone();
      case STEP.ERROR:
        return renderError();
      case STEP.EMAIL:
      default:
        return renderEmail();
    }
  };
  // #endregion renderers

  return (
    <Modal
      id="mp-register-modal"
      isOpen={isOpen}
      style={modalStyles}
      contentLabel="Create an account"
      onRequestClose={onClose}
      ariaHideApp={false}
    >
      <img alt="" src={makeAssetLink('logo.png')} style={styles.logo} />
      {renderBody()}
    </Modal>
  );
}

RegisterModal.defaultProps = {
  onClose: () => null,
  onUnavailable: () => null,
};

RegisterModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  /** Called when marketplace-api reports native registration isn't available for this marketplace. */
  onUnavailable: PropTypes.func,
};
